import React from "react";
import { Dumbbell } from "lucide-react";
import { API_ROOT } from "../config";
import TrainingPageHeader from "../components/TrainingPageHeader";
import TrainingTextEditor from "../components/TrainingTextEditor";
import TrainingVideoEditor from "../components/TrainingVideoEditor";
import TrainingSliderEditor from "../components/TrainingSliderEditor";
import TrainingAccordionEditor from "../components/TrainingAccordionEditor";
import TrainingQuizEditor from "../components/TrainingQuizEditor";
import "../styles/TrainingAddForm.css";
import { getResponseError, getNetworkError } from "../utils/fetchError";

class TrainingAddForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Training info
      trainingName: "",
      trainingDescription: "",
      trainingPortrait: "",
      portraitError: "",
      portraitFileName: null,
      mandatory: false,

      // Block form
      blockName: "",
      blockMode: "",
      blockPassingScore: "",
      blocks: [],
      selectedBlockIndex: null,

      // Section form
      sectionName: "",
      sectionType: "",
      selectedSectionIndex: null,

      // Submit
      submitStatus: null, // null | 'loading' | 'success' | 'error'
      submitError: "",
    };
  }

  handlePortraitFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      this.setState({ portraitError: "La imagen supera el límite de 2 MB. Optimízala antes de subirla." });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) =>
      this.setState({ trainingPortrait: e.target.result, portraitError: "" });
    reader.readAsDataURL(file);
  };

  handleAddBlock = () => {
    const { blockName, blockMode, blockPassingScore, blocks } = this.state;

    const initalSections =
      blockMode === "teoria"
        ? [{ name: "Introduccion", type: "text", order: 1, textContent: { body: "" } }]
        : blockMode === "test"
        ? [{ name: "Examen", type: "quiz", order: 1, quizContent: { penalized: false, questions: [] } }]
        : [];

    if (!blockName.trim() || !blockMode) return;
    this.setState({
      blocks: [
        ...blocks,
        {
          name: blockName.trim(),
          mode: blockMode,
          pass: blockPassingScore !== "" ? Number(blockPassingScore) : null,
          sections: initalSections,
        },
      ],
      blockPassingScore: "",
      blockName: "",
      blockMode: "",
    });
  };

  handleRemoveBlock = (index) => {
    this.setState((prev) => ({
      blocks: prev.blocks.filter((_, i) => i !== index),
      selectedBlockIndex:
        prev.selectedBlockIndex === index ? null : prev.selectedBlockIndex,
      selectedSectionIndex:
        prev.selectedBlockIndex === index ? null : prev.selectedSectionIndex,
    }));
  };

  handleSelectBlock = (index) => {
    this.setState((prev) => ({
      selectedBlockIndex: prev.selectedBlockIndex === index ? null : index,
      selectedSectionIndex: null,
    }));
  };

  handleAddSection = () => {
    const { sectionName, sectionType, blocks, selectedBlockIndex } = this.state;
    if (!sectionName.trim() || !sectionType || selectedBlockIndex === null)
      return;
    const currentBlock = blocks[selectedBlockIndex];
    if (currentBlock.mode === "test" && currentBlock.sections.length > 0) return;
    const defaultContent = {
      text: { textContent: { body: "" } },
      video: { videoContent: { youtubeId: "", requiredPercent: 85 } },
      slider: { sliderContent: { cards: [] } },
      accordion: { accordionContent: { items: [] } },
      quiz: { quizContent: { penalized: false, questions: [] } },
    };
    const updatedBlocks = blocks.map((block, i) => {
      if (i !== selectedBlockIndex) return block;
      return {
        ...block,
        sections: [
          ...block.sections,
          {
            name: sectionName.trim(),
            type: sectionType,
            order: block.sections.length + 1,
            ...defaultContent[sectionType],
          },
        ],
      };
    });
    this.setState({ blocks: updatedBlocks, sectionName: "", sectionType: "" });
  };

  handleSelectSection = (index) => {
    this.setState((prev) => ({
      selectedSectionIndex: prev.selectedSectionIndex === index ? null : index,
    }));
  };

  updateSectionField = (contentKey, value) => {
    const { blocks, selectedBlockIndex, selectedSectionIndex } = this.state;
    const updatedBlocks = blocks.map((block, bi) => {
      if (bi !== selectedBlockIndex) return block;
      return {
        ...block,
        sections: block.sections.map((sec, si) =>
          si !== selectedSectionIndex ? sec : { ...sec, [contentKey]: value },
        ),
      };
    });
    this.setState({ blocks: updatedBlocks });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const {
      trainingName,
      trainingDescription,
      trainingPortrait,
      mandatory,
      blocks,
    } = this.state;

    const validationErrors = [];
    if (!trainingName.trim()) validationErrors.push("El nombre de la formación es obligatorio.");
    if (blocks.length === 0) validationErrors.push("Añade al menos un bloque.");
    blocks.forEach((block, i) => {
      if (block.sections.length === 0)
        validationErrors.push(`El bloque "${block.name}" (${i + 1}) no tiene secciones.`);
    });
    if (validationErrors.length > 0) {
      this.setState({ submitStatus: "error", submitError: validationErrors.join(" ") });
      return;
    }

    const payload = {
      name: trainingName.trim(),
      description: trainingDescription.trim(),
      portrait: trainingPortrait || null,
      mandatory,
      blocks: blocks.map((block, index) => ({
        name: block.name,
        mode: block.mode,
        pass: block.pass,
        order: index + 1,
        sections: block.sections.map((sec, si) => ({
          name: sec.name,
          type: sec.type,
          order: si + 1,
          ...(sec.type === "text" && { textContent: sec.textContent }),
          ...(sec.type === "video" && { videoContent: sec.videoContent }),
          ...(sec.type === "slider" && { sliderContent: sec.sliderContent }),
          ...(sec.type === "accordion" && {
            accordionContent: sec.accordionContent,
          }),
          ...(sec.type === "quiz" && { quizContent: sec.quizContent }),
        })),
      })),
    };

    this.setState({ submitStatus: "loading", submitError: "" });
    try {
      const token = localStorage.getItem("token");
      const body = JSON.stringify(payload);
      const bodySizeKB = new Blob([body]).size / 1024;
      if (bodySizeKB > 4500) {
        this.setState({
          submitStatus: "error",
          submitError: `El payload es demasiado grande (${bodySizeKB.toFixed(0)} KB). Optimiza la imagen de portada antes de subir.`,
        });
        return;
      }
      const res = await fetch(`${API_ROOT}formations`,{
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
      });

      if (!res.ok) {
        const message = await getResponseError(res);
        this.setState({ submitStatus: "error", submitError: message });
        return;
      }

      await res.json();
      this.setState({ submitStatus: "success" });
      this.props.history.push("/training-admin");
    } catch (err) {
      this.setState({ submitStatus: "error", submitError: getNetworkError(err) });
    }
  };

  renderContentEditor() {
    const { blocks, selectedBlockIndex, selectedSectionIndex } = this.state;
    if (selectedBlockIndex === null || selectedSectionIndex === null) {
      return (
        <div className="content-editor-empty">
          <p>Selecciona una seccion para editar su contenido</p>
        </div>
      );
    }
    const section = blocks[selectedBlockIndex].sections[selectedSectionIndex];
    const typeLabels = {
      text: "Texto",
      video: "Video",
      slider: "Slider",
      accordion: "Accordion",
      quiz: "Quiz",
    };
    return (
      <div className="content-editor-inner">
        <div className="content-editor-header mb-3">
          <span className="form-label mb-0">{section.name}</span>
          <span className={`blocks-list-badge badge-${section.type}`}>
            {typeLabels[section.type]}
          </span>
        </div>
        {section.type === "text" && (
          <TrainingTextEditor
            value={section.textContent?.body}
            image={section.textContent?.image}
            rows={14}
            onChange={(val) => this.updateSectionField("textContent", val)}
          />
        )}
        {section.type === "video" && (
          <TrainingVideoEditor
            videoContent={section.videoContent}
            onChange={(val) => this.updateSectionField("videoContent", val)}
          />
        )}
        {section.type === "slider" && (
          <TrainingSliderEditor
            cards={section.sliderContent?.cards || []}
            onChange={(cards) =>
              this.updateSectionField("sliderContent", { cards })
            }
          />
        )}
        {section.type === "accordion" && (
          <TrainingAccordionEditor
            items={section.accordionContent?.items || []}
            onChange={(items) =>
              this.updateSectionField("accordionContent", { items })
            }
          />
        )}
        {section.type === "quiz" && (
          <TrainingQuizEditor
            quizContent={section.quizContent}
            onChange={(val) => this.updateSectionField("quizContent", val)}
          />
        )}
      </div>
    );
  }

  render() {
    const {
      trainingName,
      trainingDescription,
      trainingPortrait,
      portraitError,
      mandatory,
      ordered,
      blockName,
      blockMode,
      blockPassingScore,
      blocks,
      selectedBlockIndex,
      sectionName,
      sectionType,
      selectedSectionIndex,
      submitStatus,
      submitError,
    } = this.state;

    return (
      <>
        <TrainingPageHeader
          icon={<Dumbbell size={30} />}
          title="Training Admin"
        />

        <div className="training-add-form">
          <div className="training-add-form-header sticky-top mb-0 d-flex align-items-center justify-content-between w-100 h-auto p-3">
            <h2 className="training-add-form-title">Add New Training</h2>
          </div>

          <div className="training-add-form-body p-3">
            <form
              className="training-add-form-content p-3"
              onSubmit={this.handleSubmit}
            >
              <div className="training-main-layout">
                {/* Left column */}
                <div className="training-left-col">
                  {/* Training Info */}
                  <div className="training-card training-accent-total training-accent-info mb-3">
                    <div className="form-card  p-3">
                      <div className="form-group mb-3">
                        <span className = " mb-3 text-muted" >Debe de ser un nombre unico!</span>
                        <label htmlFor="trainingName" className="form-label">
                          Nombre
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="trainingName"
                          placeholder="Nombre de la formacion"
                          value={trainingName}
                          onChange={(e) =>
                            this.setState({ trainingName: e.target.value })
                          }
                        />
                      </div>
                      <div className="form-group mb-3">
                        <label
                          htmlFor="trainingDescription"
                          className="form-label"
                        >
                          Descripcion
                        </label>
                        <textarea
                          className="form-control"
                          id="trainingDescription"
                          rows="3"
                          placeholder="Descripcion de la formacion"
                          value={trainingDescription}
                          onChange={(e) =>
                            this.setState({
                              trainingDescription: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="form-group mb-3">
                        <label className="form-label">Portada</label>
                        <p className="form-text text-muted">Selecciona una imagen para la portada de la formación, Máximo 2 MB.</p>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            cursor: "pointer",
                            width: "100%",
                          }}
                        >
                          <span
                            style={{
                              padding: "6px 14px",
                              borderRadius: 6,
                              border: "1px solid #ced4da",
                              background: "#f8f9fa",
                              color: "#495057",
                              fontSize: 14,
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              userSelect: "none",
                            }}
                          >
                            Elegir imagen
                          </span>
                          <span
                            id="portrait-file-name"
                            style={{
                              flex: 1,
                              fontSize: 14,
                              color: "#6c757d",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {this.state.portraitFileName || "Sin archivo seleccionado"}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              this.setState({ portraitFileName: file ? file.name : null });
                              this.handlePortraitFile(file);
                            }}
                          />
                        </label>
                        {portraitError && (
                          <div className="text-danger small mt-1">
                            {portraitError}
                          </div>
                        )}
                        {trainingPortrait && (
                          <div className="mt-2 d-flex align-items-center gap-2">
                            <img
                              src={trainingPortrait}
                              alt="preview"
                              style={{
                                maxHeight: 80,
                                maxWidth: 120,
                                objectFit: "contain",
                                borderRadius: 4,
                                border: "1px solid #e2e8f0",
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                this.setState({ trainingPortrait: "" })
                              }
                            >
                              <i className="fas fa-times" /> Quitar
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="form-group d-flex align-items-start">
                        <input
                          type="checkbox"
                          className="training-checkbox"
                          id="mandatory"
                          checked={mandatory}
                          onChange={(e) =>
                            this.setState({ mandatory: e.target.checked })
                          }
                        />
                        <label
                          className="training-checkbox-label"
                          htmlFor="mandatory"
                        >
                          Mostrar/Ocultar <span style={{fontSize : "12px"}} className="text-muted">(mostrar o ocultar la formacion)</span>
                        </label>
                      </div>
                      <div className="form-group d-flex align-items-start">
                        <input
                          type="checkbox"
                          className="training-checkbox"
                          id="ordered"
                          checked={ordered}
                          onChange={(e) =>
                            this.setState({ ordered: e.target.checked })
                          }
                        />
                        <label
                          className="training-checkbox-label"
                          htmlFor="ordered"
                        >
                          Ordenada <span className="text-muted" style={{fontSize : "12px"}}>(si se marca los usuarios tienen que completar todas las formaciones previas para acceder, si no es siempre accesible)</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Blocks */}
                  <div className="training-card training-accent-total training-accent-blocks mb-3">
                    <div className="form-card  p-3">
                      <div className="blocks-card-inner">
                        <div className="blocks-form-side">
                          <div className="form-group mb-3">
                            <label className="form-label">
                              Nombre del bloque
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Nombre del bloque"
                              value={blockName}
                              onChange={(e) =>
                                this.setState({ blockName: e.target.value })
                              }
                            />
                          </div>
                          <div className="form-group mb-3">
                            <label className="form-label">Modo</label>
                            <select
                              className="form-control"
                              value={blockMode}
                              onChange={(e) =>
                                this.setState({
                                  blockMode: e.target.value,
                                  blockPassingScore: "",
                                })
                              }
                            >
                              <option value="">Selecciona un modo...</option>
                              <option value="teoria">Teoria</option>
                              <option value="test">Test</option>
                            </select>
                          </div>
                          {blockMode === "test" && (
                            <div className="form-group mb-3">
                              <label className="form-label">
                                Nota min. (%)
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="80"
                                min="0"
                                max="100"
                                value={blockPassingScore}
                                onChange={(e) =>
                                  this.setState({
                                    blockPassingScore: e.target.value,
                                  })
                                }
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={this.handleAddBlock}
                          >
                            Anadir bloque
                          </button>
                        </div>
                        <div className="blocks-list-side">
                          <p className="form-label mb-2">Bloques anadidos</p>
                          {blocks.length === 0 ? (
                            <div className="blocks-list-empty">
                              Sin bloques aun
                            </div>
                          ) : (
                            <ul className="blocks-list">
                              {blocks.map((block, i) => (
                                <li
                                  key={i}
                                  className={`blocks-list-item ${selectedBlockIndex === i ? "selected" : ""}`}
                                  onClick={() => this.handleSelectBlock(i)}
                                >
                                  <span className="blocks-list-index">
                                    {i + 1}
                                  </span>
                                  <span className="blocks-list-name">
                                    {block.name}
                                  </span>
                                  <span
                                    className={`blocks-list-badge badge-${block.mode}`}
                                  >
                                    {block.mode}
                                  </span>
                                  <span className="blocks-list-score">
                                    {block.pass !== null &&
                                    block.pass !== undefined
                                      ? `Min: ${block.pass}%`
                                      : "Sin mín."}
                                  </span>
                                  <button
                                    type="button"
                                    className="blocks-list-remove"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.handleRemoveBlock(i);
                                    }}
                                  >
                                    x
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sections */}
                  <div className="training-card training-accent-total training-accent-sections mb-3">
                    <div className="form-card p-3 ">
                      <p className="form-label mb-3">
                        {selectedBlockIndex !== null ? (
                          <span>
                            Secciones de:{" "}
                            <strong>{blocks[selectedBlockIndex].name}</strong>
                          </span>
                        ) : (
                          "Secciones - selecciona un bloque"
                        )}
                      </p>
                      {selectedBlockIndex === null ? (
                        <div className="blocks-list-empty">
                          Haz click en un bloque para gestionar sus secciones
                        </div>
                      ) : (
                        <div className="blocks-card-inner">
                          {!(blocks[selectedBlockIndex].mode === "test" && blocks[selectedBlockIndex].sections.length > 0) && <div className="blocks-form-side">
                            <div className="form-group mb-3">
                              <label className="form-label">Nombre</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Nombre de la seccion"
                                value={sectionName}
                                onChange={(e) =>
                                  this.setState({ sectionName: e.target.value })
                                }
                              />
                            </div>
                            <div className="form-group mb-3">
                              <label className="form-label">Tipo</label>
                              <select
                                className="form-control"
                                value={sectionType}
                                onChange={(e) =>
                                  this.setState({ sectionType: e.target.value })
                                }
                              >
                                <option value="">Selecciona un tipo...</option>
                                {blocks[selectedBlockIndex].mode === "test" ? (
                                  <option value="quiz">Quiz</option>
                                ) : (
                                  <>
                                    <option value="text">Texto</option>
                                    <option value="video">Video</option>
                                    <option value="slider">Slider</option>
                                    <option value="accordion">Accordion</option>
                                    <option value="quiz">Quiz</option>
                                  </>
                                )}
                              </select>
                            </div>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={this.handleAddSection}
                            >
                              Anadir seccion
                            </button>
                          </div>}
                          <div className="blocks-list-side">
                            <p className="form-label mb-2">
                              Secciones anadidas
                            </p>
                            {blocks[selectedBlockIndex].sections.length ===
                            0 ? (
                              <div className="blocks-list-empty">
                                Sin secciones aun
                              </div>
                            ) : (
                              <ul className="blocks-list">
                                {blocks[selectedBlockIndex].sections.map(
                                  (sec, i) => (
                                    <li
                                      key={i}
                                      className={`blocks-list-item ${selectedSectionIndex === i ? "selected" : ""}`}
                                      onClick={() =>
                                        this.handleSelectSection(i)
                                      }
                                    >
                                      <span className="blocks-list-index">
                                        {i + 1}
                                      </span>
                                      <span className="blocks-list-name">
                                        {sec.name}
                                      </span>
                                      <span
                                        className={`blocks-list-badge badge-${sec.type}`}
                                      >
                                        {sec.type}
                                      </span>
                                    </li>
                                  ),
                                )}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right column: content editor */}
                <div className="training-right-col">
                  <div className="training-card training-accent-total training-accent-editor mb-3">
                    <div className="form-card p-3 content-editor-card">
                      <p className="form-label mb-3">Editor de contenido</p>
                      {this.renderContentEditor()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="submit-area mt-3">
                {submitStatus === "success" && (
                  <div className="submit-feedback success">
                    Formacion creada correctamente.
                  </div>
                )}
                {submitStatus === "error" && (
                  <div className="submit-feedback error">{submitError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitStatus === "loading"}
                >
                  {submitStatus === "loading" ? (
                    <>
                      <span className="submit-spinner" /> Creando...
                    </>
                  ) : (
                    "Crear formacion"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  }
}

export default TrainingAddForm;
